import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Star, MapPin, Package, Zap, Video } from 'lucide-react';
import { getVendorConsultationSettings } from '../../services/consultationService';
import BookConsultationModal from './BookConsultationModal';
import '../../styles/bookingWidget.css';

const BookingWidget = ({
  service,
  vendor,
  category,
  startingPrice,
  hasPackages,
  serviceRating,
  serviceReviews,
  isAuthenticated,
  navigate,
  formatPrice,
}) => {
  const [showConsultationModal, setShowConsultationModal] = useState(false);
  const vendorId = vendor?.id;
  const { data: consultationData } = useQuery({
    queryKey: ['vendorConsultationSettings', vendorId],
    queryFn: () => getVendorConsultationSettings(vendorId),
    enabled: !!vendorId,
  });
  const consultationEnabled = consultationData?.data?.consultation_enabled ?? false;
  const consultationQuestions = consultationData?.data?.consultation_questions ?? [];
  const packageCount = service?.vendor_service_packages?.length || 0;

  const goBook = () => {
    if (isAuthenticated) {
      navigate(`/booking/${service.id}`);
    } else {
      navigate('/login', {
        state: { from: { pathname: `/booking/${service.id}` } },
      });
    }
  };

  return (
    <div className="rounded-2xl border border-m-border-light bg-m-surface p-6 shadow-[0_12px_40px_rgba(0,0,0,0.06)]">
      <div className="mb-6">
        <div className="mb-5 flex items-baseline gap-2">
          <span className="text-2xl font-medium tracking-tight text-m-ink">
            ${formatPrice(startingPrice)}
          </span>
          <span className="text-sm text-m-ink-muted">starting from</span>
        </div>

        {hasPackages ? (
          <>
            <button
              type="button"
              onClick={goBook}
              className="marketing-btn marketing-btn-ink w-full gap-2"
            >
              <Zap className="h-4 w-4" />
              Book this service
            </button>
            <p className="mt-3 text-center text-xs text-m-ink-muted">
              You won’t be charged yet
            </p>
            {isAuthenticated && consultationEnabled ? (
              <button
                type="button"
                onClick={() => setShowConsultationModal(true)}
                className="mt-3 inline-flex h-[var(--m-control-h)] w-full items-center justify-center gap-2 rounded-full border border-m-border-light text-sm font-medium text-m-ink transition-colors hover:border-m-ink"
              >
                <Video className="h-4 w-4" />
                Book consultation
              </button>
            ) : null}
          </>
        ) : (
          <>
            <button
              type="button"
              disabled
              className="marketing-btn marketing-btn-ink w-full gap-2 opacity-50"
            >
              <Zap className="h-4 w-4" />
              Book this service
            </button>
            <p className="mt-3 text-center text-xs text-m-ink-muted">
              Packages not added yet. Please check back later to book.
            </p>
          </>
        )}
      </div>

      <div className="border-t border-m-border-light pt-6">
        <div className="space-y-4 text-sm">
          <div className="flex items-center justify-between">
            <span className="text-m-ink-muted">Starting price</span>
            <span className="font-medium text-m-ink">${formatPrice(startingPrice)}</span>
          </div>

          {category ? (
            <div className="flex items-center justify-between">
              <span className="text-m-ink-muted">Category</span>
              <span className="font-medium capitalize text-m-ink">
                {category.category_name}
              </span>
            </div>
          ) : null}

          {packageCount > 0 ? (
            <div className="flex items-center justify-between">
              <span className="text-m-ink-muted">Packages</span>
              <div className="flex items-center gap-1">
                <Package className="h-4 w-4 text-m-ink-muted" />
                <span className="font-medium text-m-ink">{packageCount}</span>
              </div>
            </div>
          ) : null}

          {serviceRating > 0 ? (
            <div className="flex items-center justify-between">
              <span className="text-m-ink-muted">Rating</span>
              <div className="flex items-center gap-1">
                <Star className="h-4 w-4 fill-current text-m-ink" />
                <span className="font-medium text-m-ink">
                  {typeof serviceRating === 'number' && !isNaN(serviceRating)
                    ? serviceRating.toFixed(1)
                    : '0.0'}
                  {serviceReviews > 0 ? (
                    <span className="ml-1 text-m-ink-muted">({serviceReviews})</span>
                  ) : null}
                </span>
              </div>
            </div>
          ) : null}

          {vendor?.city && vendor?.state ? (
            <div className="flex items-center justify-between">
              <span className="text-m-ink-muted">Location</span>
              <div className="flex items-center gap-1">
                <MapPin className="h-4 w-4 text-m-ink-muted" />
                <span className="font-medium text-m-ink">
                  {vendor.city}, {vendor.state}
                </span>
              </div>
            </div>
          ) : null}
        </div>
      </div>

      <BookConsultationModal
        isOpen={showConsultationModal}
        onClose={() => setShowConsultationModal(false)}
        vendorId={vendorId}
        vendorName={vendor?.business_name}
        serviceId={service?.id}
        questions={consultationQuestions}
      />
    </div>
  );
};

export default BookingWidget;
